import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';

import { ClientAccess } from '@core/models/client-access';
import { ACCESS_STORE } from '@core/stores/access.store';
import { APP_STORE } from '@core/stores/app.store';
import { USER_STATE_STORE } from '@core/stores/user-state.store';
import { USER_STORE } from '@core/stores/user.store';
import { Account } from '@shared/models/account';
import { AccountType } from '@shared/models/account-type';
import { User } from '@shared/models/user';
import { UserState } from '@shared/models/user-state';
import { ApplicationInsightsService } from '@shared/services/application-insights.service';
import { ClarityService } from '@shared/services/clarity.service';
import { RouterService } from '@shared/services/router.service';
import { waitForSignalLoaded } from '@shared/utils/wait-for-signal-loaded';
import { ENVIRONMENT } from '@env/environment';
import { AuthService } from '@auth/services/auth/auth.service';

@Injectable()
export class AppInitializerService {
  readonly #injector = inject(Injector);
  readonly #clarityService = inject(ClarityService);
  readonly #authService = inject(AuthService);
  readonly #appStore = inject(APP_STORE);
  readonly #accessStore = inject(ACCESS_STORE);
  readonly #userStore = inject(USER_STORE);
  readonly #userStateStore = inject(USER_STATE_STORE);

  router: Router;
  routerService: RouterService;

  constructor() {
    // it is not possible to use routes inside APP_INITIALIZER
    this.router = this.#injector.get(Router);
    this.routerService = this.#injector.get(RouterService);
  }

  async init(): Promise<void> {
    await this.#authService.init();

    await this.loadStartupData();

    this.#clarityService.init();
    this.initializeApplicationInsights();

    const clientIdFromURL = this.getParamByName('clientId');
    let clientsIds: string[] = [];
    let selectedClientId: string;
    let redirectTo = this.routerService.currentFullRoute;

    const user = this.#userStore.data();
    const userState = this.#userStateStore.data();

    // user has no account
    if (!user?.accounts?.length) {
      void this.router.navigate(['/error'], {
        queryParams: {
          errorType: 'no-account'
        }
      });

      return;
    }

    // select account
    const selectedAccount = this.selectAccount(
      user,
      userState,
      clientIdFromURL
    );

    if (user.accounts.some(x => x.accountType === AccountType.SystemAdmin)) {
      clientsIds = (await this.loadClients()).map(x => x.id);

      // there is no client available
      if (!clientsIds?.length) {
        void this.router.navigate(['/error'], {
          queryParams: {
            errorType: 'no-client'
          }
        });

        return;
      }

      selectedClientId = this.selectClient(
        clientsIds,
        userState,
        clientIdFromURL
      );
    } else {
      if (!selectedAccount.client?.modules.includes(ENVIRONMENT.moduleId)) {
        void this.router.navigate(['/error'], {
          queryParams: {
            errorType: 'no-module-access'
          }
        });

        this.#appStore.setupClientSuccess(selectedClientId);

        return;
      }

      clientsIds = user.accounts.map(x => x.client?.id);
      selectedClientId = selectedAccount.client.id;
    }

    // the client id from url is not available
    if (clientIdFromURL && !clientsIds.includes(clientIdFromURL)) {
      redirectTo = 'page-not-found';
    }

    // query params are binding to component inputs through withComponentInputBinding from app.config.ts
    void this.router.navigate(['/client-setup'], {
      queryParams: {
        clientId: selectedClientId,
        projectId: this.getParamByName('projectId'),
        redirectTo
      }
    });
  }

  selectClient(
    clientsIds: string[],
    userState: UserState,
    clientIdFromURL: string
  ): string {
    const clientId =
      // select client from URL
      clientsIds.find(x => x === clientIdFromURL) ||
      // select client from userState
      clientsIds.find(x => x === userState?.lastSelectedClientId) ||
      // select first client
      clientsIds[0];

    this.#appStore.selectClient(clientId);

    return clientId;
  }

  selectAccount(
    user: User,
    userState: UserState,
    clientIdFromURL: string
  ): Account {
    const account =
      // select account from URL
      user.accounts.find(x => x.client?.id === clientIdFromURL) ||
      // select account from userState
      user.accounts.find(
        x => x.client?.id === userState?.lastSelectedClientId
      ) ||
      // select first account
      user.accounts[0];

    this.#appStore.selectAccount(account.id, account.client?.id);

    return account;
  }

  async loadClients(): Promise<ClientAccess[]> {
    this.#accessStore.loadClients();

    await waitForSignalLoaded(this.#accessStore.clients.loaded, this.#injector);

    return this.#accessStore.clients().entities;
  }

  initializeApplicationInsights(): void {
    if (!ENVIRONMENT.appInsights.enabled) {
      return;
    }

    const userEmail = `${this.#authService.getActiveAccount()?.idTokenClaims?.['email']}`;

    // it is not possible to use routes inside APP_INITIALIZER,
    const applicationInsightsService = this.#injector.get(
      ApplicationInsightsService
    );

    applicationInsightsService.setUserId(userEmail);
  }

  //#region Private methods

  private async loadStartupData(): Promise<void> {
    this.#userStore.loadUser();
    this.#userStateStore.loadState();
    this.#accessStore.loadModules();

    await Promise.all([
      waitForSignalLoaded(this.#userStore.loaded, this.#injector),
      waitForSignalLoaded(this.#userStateStore.loaded, this.#injector),
      waitForSignalLoaded(this.#accessStore.modules.loaded, this.#injector)
    ]);
  }

  private getParamByName(name: string): string {
    const queryString = window.location.search;
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      const param = urlParams.get(name);
      if (param) {
        return param;
      }
    }

    try {
      let position = 0;
      switch (name) {
        case 'clientId':
          position = 1;
          break;
        case 'projectId':
          position = 2;
          break;
      }

      return window.location.pathname.split('/')[position];
    } catch {
      return '';
    }
  }

  //#endregion
}
